// Gedeelde betaallogica voor server en browser. Puur, zonder Node-specifieke imports.

const REF_BASE_LENGTH = 10;
const REF_LENGTH = 12;
const EPC_NAME_MAX = 70;
const EURO_AMOUNT_PATTERN = /^(\d+)(?:[.,](\d{1,2}))?$/;

function checkDigitFor(base: string): string {
  const remainder = Number(base) % 97;
  return String(remainder === 0 ? 97 : remainder).padStart(2, '0');
}

/** 12 cijfers: eerste 1–9, dan 9 willekeurige cijfers, dan 2 controlecijfers (mod 97, 0 → 97). */
export function createRefDigits(random: () => number = Math.random): string {
  let base = String(1 + Math.floor(random() * 9));
  for (let i = 1; i < REF_BASE_LENGTH; i++) base += String(Math.floor(random() * 10));
  return base + checkDigitFor(base);
}

export function isValidRefDigits(digits: string): boolean {
  if (!/^[1-9]\d{11}$/.test(digits)) return false;
  return checkDigitFor(digits.slice(0, REF_BASE_LENGTH)) === digits.slice(REF_BASE_LENGTH);
}

/** Zet 12 cijfers om naar de weergave +++123/4567/89002+++. */
export function formatRef(digits: string): string {
  return `+++${digits.slice(0, 3)}/${digits.slice(3, 7)}/${digits.slice(7)}+++`;
}

/** Houdt alleen de cijfers over, zodat ook een opgemaakte mededeling werkt. */
export function refDigits(text: string): string {
  return text.replace(/\D/g, '');
}

export function cleanIban(s: string): string {
  return s.replace(/\s+/g, '').toUpperCase();
}

export function isValidIban(s: string): boolean {
  const iban = cleanIban(s);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{8,30}$/.test(iban)) return false;
  const rearranged = (iban.slice(4) + iban.slice(0, 4)).replace(
    /[A-Z]/g,
    (ch) => String(ch.charCodeAt(0) - 55),
  );
  let remainder = 0;
  for (const digit of rearranged) remainder = (remainder * 10 + Number(digit)) % 97;
  return remainder === 1;
}

export function prettyIban(s: string): string {
  return cleanIban(s).replace(/(.{4})/g, '$1 ').trim();
}

/** Bedrag in gehele centen naar de EPC-notatie "EUR195.00". */
function formatEpcAmount(amountCents: number): string {
  const euros = Math.trunc(amountCents / 100);
  const cents = String(amountCents % 100).padStart(2, '0');
  return `EUR${euros}.${cents}`;
}

/** EPC/Girocode-payload (SCT) voor de betaal-QR-code. Bedrag in gehele centen. */
export function epcPayload(name: string, iban: string, amountCents: number, ref: string): string {
  if (!Number.isSafeInteger(amountCents) || amountCents < 0) {
    throw new RangeError('amountCents moet een niet-negatief geheel getal zijn');
  }
  return [
    'BCD',
    '002',
    '1',
    'SCT',
    '',
    name.slice(0, EPC_NAME_MAX),
    cleanIban(iban),
    formatEpcAmount(amountCents),
    '',
    '',
    ref,
  ].join('\n');
}

/**
 * Leest een invoer als "€ 12,50" of "12.5" en geeft gehele centen terug.
 * Geeft null bij 0, negatieve, niet-numerieke of meer dan 2 decimalen.
 */
export function parseEuroToCents(input: string): number | null {
  if (typeof input !== 'string') return null;
  const normalized = input.replace(/€/g, '').replace(/\s+/g, '');
  const match = EURO_AMOUNT_PATTERN.exec(normalized);
  if (!match) return null;
  const euros = Number(match[1]);
  const cents = Number((match[2] ?? '').padEnd(2, '0'));
  const total = euros * 100 + cents;
  if (!Number.isSafeInteger(total) || total === 0) return null;
  return total;
}
